import React, { useState } from "react";
import {
  Alert,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import * as SC from "SC";
import { httpService } from "api/httpService";

const ROLES = [
  { value: "dispatcher", label: "Dispatcher" },
  { value: "admin", label: "Admin" },
  { value: "driver", label: "Driver" },
];

const newInvite = () => ({ email: "", role: "dispatcher" });

export const InviteStep = ({ onDone, onSkip }) => {
  const [invites, setInvites] = useState([newInvite()]);
  const [status, setStatus] = useState({ loading: false, error: null, results: [] });

  const update = (i, field) => (e) => {
    const value = e.target.value;
    setInvites((list) => list.map((row, idx) => (idx === i ? { ...row, [field]: value } : row)));
  };

  const addRow = () => setInvites((list) => [...list, newInvite()]);
  const removeRow = (i) =>
    setInvites((list) => (list.length === 1 ? list : list.filter((_, idx) => idx !== i)));

  const submit = async () => {
    const filled = invites.filter((row) => row.email.trim());
    if (filled.length === 0) {
      setStatus({ loading: false, error: "Add at least one email or skip this step." });
      return;
    }
    const invalid = filled.find((row) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email.trim()));
    if (invalid) {
      setStatus({ loading: false, error: `Invalid email: ${invalid.email}` });
      return;
    }
    setStatus({ loading: true, error: null, results: [] });

    const results = [];
    for (const row of filled) {
      const payload = { email: row.email.trim().toLowerCase(), role: row.role };
      const { value, error } = await httpService({
        url: "/invitations/send",
        options: { method: "POST", data: payload },
      });
      if (error) {
        if (error.toLowerCase().includes("unauthorized")) {
          const pending = JSON.parse(
            window.sessionStorage.getItem("fleetmd.onboarding.invites") || "[]"
          );
          pending.push(payload);
          window.sessionStorage.setItem(
            "fleetmd.onboarding.invites",
            JSON.stringify(pending)
          );
          results.push({ email: payload.email, status: "queued" });
          continue;
        }
        results.push({ email: payload.email, status: "error", detail: error });
      } else {
        results.push({ email: value?.email || payload.email, status: "sent" });
      }
    }

    const hasErrors = results.some((r) => r.status === "error");
    setStatus({
      loading: false,
      error: hasErrors ? "Some invitations could not be sent — see details below." : null,
      results,
    });

    if (!hasErrors) {
      setTimeout(() => onDone(results), 600);
    }
  };

  return (
    <Stack gap={2.5}>
      <Stack>
        <SC.AuthTitle sx={{ fontSize: "1.4rem", fontWeight: 700 }}>
          Invite your team
        </SC.AuthTitle>
        <Typography variant="body2" color="text.secondary">
          We'll email each person a secure link to join your company.
        </Typography>
      </Stack>

      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent>
          <Stack gap={2}>
            {invites.map((row, i) => (
              <Stack key={i} direction={{ xs: "column", sm: "row" }} gap={1.5} alignItems="center">
                <OutlinedInput
                  size="small"
                  placeholder="teammate@company.com"
                  value={row.email}
                  onChange={update(i, "email")}
                  sx={{ flex: 2 }}
                  fullWidth
                />
                <Select
                  size="small"
                  value={row.role}
                  onChange={update(i, "role")}
                  input={<OutlinedInput size="small" />}
                  sx={{ minWidth: 140 }}
                >
                  {ROLES.map((r) => (
                    <MenuItem key={r.value} value={r.value}>
                      {r.label}
                    </MenuItem>
                  ))}
                </Select>
                <IconButton
                  onClick={() => removeRow(i)}
                  disabled={invites.length === 1}
                  size="small"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
            <SC.ControlButton
              variant="outlined"
              onClick={addRow}
              sx={{ alignSelf: "flex-start", width: "auto" }}
              startIcon={<AddIcon />}
            >
              Add another
            </SC.ControlButton>
          </Stack>
        </CardContent>
      </Card>

      {status.error && <Alert severity="warning">{status.error}</Alert>}

      {status.results.length > 0 && (
        <Stack gap={0.5}>
          {status.results.map((r) => (
            <Stack key={r.email} direction="row" alignItems="center" gap={1}>
              <Chip
                size="small"
                label={r.status}
                color={
                  r.status === "sent"
                    ? "success"
                    : r.status === "queued"
                    ? "default"
                    : "error"
                }
                sx={{ textTransform: "capitalize" }}
              />
              <Typography variant="body2">{r.email}</Typography>
              {r.detail && (
                <Typography variant="caption" color="text.secondary">
                  — {r.detail}
                </Typography>
              )}
            </Stack>
          ))}
        </Stack>
      )}

      <Stack direction="row" justifyContent="space-between" gap={1.5}>
        <SC.ControlButton variant="outlined" onClick={onSkip} disabled={status.loading}>
          Skip for now
        </SC.ControlButton>
        <SC.ControlButton
          variant="contained"
          onClick={submit}
          disabled={status.loading}
          sx={{ minWidth: 200 }}
        >
          {status.loading ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            "Send Invitations →"
          )}
        </SC.ControlButton>
      </Stack>
    </Stack>
  );
};

export default InviteStep;
